import { useDi } from '@root/Services/DI';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import { useEffect, useMemo, useState } from 'react';
import { BaseResource } from '../ResourcesGrid';
import { FillerSwitch } from '@root/Design/Filler';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { useDiMemo } from '@root/Services/DI';
import { IBaseInvoiceRecord } from '@root/Services/Invoices/InvoiceSchemaService';
import { cleanExprDates, exprBuilder, groupExprs } from '@root/Services/QueryExpr';
import { QueryExpr } from '@apis/Resources';
import { startOfMonth, endOfMonth, addDays, startOfDay } from 'date-fns';
import { DailyInvoiceGrid } from '@root/Components/Invoices/DailyInvoiceGrid';
import { useInvoiceFieldCompatibility } from '@root/Services/Invoices/InvoiceFieldCompatibilityService';
import { useInvoiceCostField } from '@root/Services/Invoices/InvoiceCostFieldContext';
import { GridGroupByState } from '@root/Components/DataGrid/Models';
import { Box, Space, Text } from '@mantine/core';
import { DateRangeFilter } from '@root/Components/Filter/DateRangeFilter';
import { withSchemaPreloader } from '@root/Components/Invoices/SchemaPreloader';

function ResourceCostDetailsBase({ resource }: { resource: BaseResource }) {
    const invoiceApi = useDiMemo(InvoiceApiService);
    const fieldCompat = useInvoiceFieldCompatibility('daily', true);
    const fmtSvc = useFmtSvc();

    const billingKey = resource.CloudPlatform === 'Azure' ? (resource as any).AzureId || '' : resource?.BillingKey || '';

    const [selectedDateRange, setSelectedDateRange] = useState<{ from?: Date; to?: Date; id?: string }>(() => {
        const now = new Date();
        const from = startOfMonth(now);
        const to = endOfMonth(now);
        return { from, to };
    });

    const [actualDateRange, setActualDateRange] = useState<{ from?: Date; to?: Date }>(() => {
        const now = new Date();
        const from = startOfMonth(now);
        const to = endOfMonth(now);
        return { from, to };
    });

    useEffect(() => {
        const pastDays =
            selectedDateRange.id === 'last-7' ? -6 : selectedDateRange.id === 'last-30' ? -29 : selectedDateRange.id === 'last-90' ? -89 : null;

        const from = pastDays ? addDays(new Date(), pastDays) : selectedDateRange.from;
        const to = pastDays ? new Date() : selectedDateRange.to;

        setActualDateRange({
            from: from ? startOfDay(from) : undefined,
            to: to ? startOfDay(to) : undefined,
        });
    }, [selectedDateRange.from, selectedDateRange.to, selectedDateRange.id]);

    const billingKeyFilter = useMemo(() => {
        if (!fieldCompat || !billingKey) {
            return undefined;
        }

        const { builder: xb } = exprBuilder<IBaseInvoiceRecord>();
        const filters = [xb.model.ResourceId!.eq(billingKey)];

        if (actualDateRange.from && actualDateRange.to) {
            const dateField = fieldCompat.getAvailableField('ChargePeriodStart')?.path || 'UsageStartDate';
            const dateExpr = xb.fromExpr<Date>({ Field: dateField });
            filters.push(dateExpr.onOrAfter(actualDateRange.from), dateExpr.onOrBefore(actualDateRange.to));
        }

        const result = groupExprs(
            'and',
            filters.map((b) => xb.resolve(b))
        ) as QueryExpr;
        return cleanExprDates(result);
    }, [fieldCompat, billingKey, actualDateRange]);

    const dateRange = useMemo(() => {
        if (!actualDateRange.from || !actualDateRange.to) {
            const now = new Date();
            const from = startOfMonth(now);
            const to = endOfMonth(now);
            return { from, to };
        }

        return {
            from: actualDateRange.from,
            to: actualDateRange.to,
        };
    }, [actualDateRange]);

    const defaultGroupBy = useMemo(() => {
        if (!fieldCompat) return [];

        const [dateFld] = fieldCompat.getValidFields('BilledDate');
        if (!dateFld) {
            return [];
        }

        return [{ id: dateFld.pathWithRoot, sortDir: 'Desc', sortMode: 'value' }] as GridGroupByState[];
    }, [fieldCompat]);

    return (
        <FillerSwitch loading={!fieldCompat} noData={!billingKey || !billingKeyFilter}>
            {() =>
                defaultGroupBy.length > 0 ? (
                    <DailyInvoiceGrid
                        key={billingKey}
                        persistenceKey="ResourceCostDetails"
                        dateRange={dateRange}
                        invoiceApi={invoiceApi}
                        defaultGroupBy={defaultGroupBy}
                        scope={billingKeyFilter}
                        rightPlaceholder={
                            <>
                                <DateRangeFilter
                                    value={selectedDateRange}
                                    onChange={setSelectedDateRange}
                                    constraint={{}}
                                    options={[
                                        { id: 'last-7', label: 'Last 7 days' },
                                        { id: 'last-30', label: 'Last 30 days' },
                                        { id: 'last-90', label: 'Last 90 days' },
                                    ]}
                                />
                                <Space w="md" />
                            </>
                        }
                    />
                ) : (
                    <Box p="md">
                        <Text color="dimmed" align="center">
                            No Data
                        </Text>
                    </Box>
                )
            }
        </FillerSwitch>
    );
}

export const ResourceCostDetails = withSchemaPreloader(ResourceCostDetailsBase);
